import { useState, useRef } from 'react';
import { Card, Input, Button, Space, App } from 'antd';
import { UploadOutlined, FileTextOutlined, ClearOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { parseNacha } from '@/utils/nachaParser';
import { trackFileUpload, trackFileParse, trackError } from '@/utils/analytics';
import type { NachaParseResult } from '@/types/nacha';
import {
  SUPPORTED_MIME_TYPES,
  FILE_READ_TIMEOUT_MS,
  isSupportedFileExtension,
  isFileSizeValid,
  formatBytesToKB,
  FILE_UPLOAD_ERRORS,
  FILE_UPLOAD_SUCCESS,
} from '@/constants/fileUpload';

const { TextArea } = Input;
const { useApp } = App;

interface FileInputProps {
  onParseComplete: (result: NachaParseResult) => void;
  onLoadingChange?: (loading: boolean, text: string) => void;
}

function FileInput({ onParseComplete, onLoadingChange }: FileInputProps) {
  const { message } = useApp();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notify parent about loading state changes
  const updateLoading = (isLoading: boolean, text: string = 'Processing...') => {
    setLoading(isLoading);
    if (onLoadingChange) {
      onLoadingChange(isLoading, text);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeKB = formatBytesToKB(file.size);
    console.log('🔵 File selected:', file.name, 'Size:', sizeKB, 'KB');
    
    // Clear previous parse result when new file is selected
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onParseComplete(null as any);
    
    // Validate file extension first (before reading)
    if (!isSupportedFileExtension(file.name)) {
      message.error({
        content: FILE_UPLOAD_ERRORS.INVALID_TYPE(file.name),
        duration: 5,
      });
      
      // Clear left panel content
      setContent('');
      setFileName('');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check file size limit
    if (!isFileSizeValid(file.size)) {
      message.error({
        content: FILE_UPLOAD_ERRORS.FILE_TOO_LARGE(sizeKB),
        duration: 5,
      });
      
      // Clear left panel content
      setContent('');
      setFileName('');
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Show local loading state (not passed to parent/right panel)
    setLoading(true);

    try {
      // Use async file reading with progress and timeout
      const text = await Promise.race([
        readFileAsync(file, (progress) => {
          // Just log progress, don't update parent loading state
          console.log(`📊 Reading progress: ${Math.round(progress)}%`);
        }),
        // Timeout after configured duration
        new Promise<string>((_, reject) => 
          setTimeout(() => reject(new Error(FILE_UPLOAD_ERRORS.READ_TIMEOUT())), FILE_READ_TIMEOUT_MS)
        )
      ]);

      console.log('✅ File loaded, length:', text.length);
      
      // Set content first so it's available
      setContent(text);
      setFileName(file.name);
      setLoading(false);
      
      // Track successful file upload
      trackFileUpload(file.size, file.name);
      
      // Reset file input to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Auto-parse the file after successful upload
      // This will handle validation and show parse errors if any
      setTimeout(() => {
        parseContent(text);
      }, 100);
      
    } catch (error) {
      console.error('❌ File reading error:', error);
      setLoading(false);
      
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      
      // Track file reading error
      trackError(errorMsg, 'File Reading');
      
      // Show error message for file reading failures
      message.error({
        content: FILE_UPLOAD_ERRORS.READ_FAILED(errorMsg),
        duration: 6,
      });
      
      // Clear content on reading error
      setContent('');
      setFileName('');
      
      // Reset file input on error
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Async file reader with progress and abort capability
  const readFileAsync = (file: File, onProgress?: (progress: number) => void): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      let lastUpdate = 0;
      let startTime = Date.now();
      
      reader.onloadstart = () => {
        console.log('📖 Starting to read file...');
        startTime = Date.now();
        if (onProgress) onProgress(0);
      };
      
      reader.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          const now = Date.now();
          const elapsed = now - startTime;
          
          // Abort if taking too long (> 25 seconds)
          if (elapsed > 25000) {
            reader.abort();
            reject(new Error('File reading is taking too long. The file may be too large or corrupted.'));
            return;
          }
          
          // Throttle updates to every 100ms for better performance
          if (now - lastUpdate > 100 || progress === 100) {
            console.log(`📊 Reading progress: ${progress.toFixed(1)}% (${(elapsed / 1000).toFixed(1)}s)`);
            onProgress(progress);
            lastUpdate = now;
          }
        }
      };
      
      reader.onload = (event) => {
        try {
          const text = event.target?.result as string;
          const elapsed = Date.now() - startTime;
          console.log(`✅ File read complete in ${(elapsed / 1000).toFixed(2)}s`);
          if (onProgress) onProgress(100);
          resolve(text);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        console.error('❌ File read error');
        reject(new Error('Failed to read file. The file may be corrupted or inaccessible.'));
      };

      reader.onabort = () => {
        console.error('⏸️ File read aborted');
        reject(new Error('File reading was aborted'));
      };
      
      reader.readAsText(file);
    });
  };

  const handleUploadClick = () => {
    console.log('🟢 Upload area clicked!');
    fileInputRef.current?.click();
  };

  const parseContent = async (textContent: string) => {
    if (!textContent.trim()) {
      message.warning('Please enter or upload NACHA file content');
      return;
    }

    updateLoading(true, 'Parsing NACHA file...');
    
    try {
      // Use setTimeout to allow loading UI to render first
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const result = await parseNacha(textContent);
      
      // Update loading text for validation
      if (result.success) {
        updateLoading(true, 'Validating file structure...');
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      onParseComplete(result);
      
      if (result.success) {
        const lines = textContent.split(/\r?\n/).filter(l => l.length > 0).length;
        
        // Track successful parse
        trackFileParse(true, lines);
        
        message.success(
          FILE_UPLOAD_SUCCESS.PARSED(lines, result.parseTime)
        );
      } else {
        // Track parse failure (no message shown, error displayed in ResultView)
        const errorMsg = result.validation.errors.length > 0 
          ? result.validation.errors[0].message 
          : 'File parsing failed';
        
        trackFileParse(false);
        trackError(errorMsg, 'File Parse');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Track parse exception (no message shown, error displayed in ResultView)
      trackError(errorMsg, 'File Parse Exception');
    } finally {
      updateLoading(false, 'Processing...');
    }
  };

  const handleParse = async () => {
    await parseContent(content);
  };

  const handleClear = () => {
    setContent('');
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Pass null to reset the result view without showing error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onParseComplete(null as any);
  };

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          File Input
        </Space>
      }
      className="file-input-card"
    >
      <Space direction="vertical" style={{ width: '100%', flex: 1 }} size="middle">
        <input
          ref={fileInputRef}
          type="file"
          accept={SUPPORTED_MIME_TYPES}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        
        <div 
          className="custom-upload-area"
          onClick={handleUploadClick}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('drag-over');
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove('drag-over');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('drag-over');
            const file = e.dataTransfer.files?.[0];
            if (file) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const event = { target: { files: [file] } } as any;
              handleFileChange(event);
            }
          }}
        >
          <div className="upload-icon">
            <UploadOutlined />
          </div>
          <p className="upload-text">Click or drag NACHA file to this area</p>
          <p className="upload-hint">Support .txt, .ach, .nacha, .dat or no extension • Text files only • Max 10MB</p>
          <p className="upload-hint" style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
            💡 Tip: For files without extension, use drag & drop or select "All Files" in file picker
          </p>
          {fileName && (
            <div className="upload-file-info">
              <FileTextOutlined /> {fileName}
            </div>
          )}
        </div>

        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Paste NACHA file content here..."
          rows={12}
          disabled={loading}
          style={{ 
            fontFamily: 'monospace', 
            fontSize: '12px',
            flex: 1,
            minHeight: '200px'
          }}
        />

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleClear}
            disabled={!content}
          >
            Clear
          </Button>
          
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleParse}
            loading={loading}
            disabled={!content.trim()}
            size="large"
          >
            Parse
          </Button>
        </Space>
      </Space>

    </Card>
  );
}

export default FileInput;
